/* ------------------------ Functional imports start ------------------------ */
import { type ReactNode, useState, type ReactElement } from 'react'
import { useImageEditorContext } from './ImageEditorContext'
import { useTranslation } from 'react-i18next'
import LogTool from '../../logger/logTools'
import { hexToRgba } from '@uiw/react-color'
/* ------------------------- Functional imports end ------------------------- */

/* -------------------------- Design imports start -------------------------- */
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { DeleteRounded, RedoRounded, UndoRounded } from '@mui/icons-material'
import ColorInput from '../inputs/ColorInput'
import PreviewModeSelect from './PreviewModeSelect'
/* --------------------------- Design imports end --------------------------- */

/**
 * Interface that describes an image editor tool.
 */
interface Tool {
  id: string // unique string that identifies the tool
  icon: ReactNode // the icon to display for the tool
  onClick: () => void // callback function that is called when the tool is used
  tooltip: string // tooltip text to display when hovering over the tool icon
}

interface Props {
  disabled?: boolean
}

/* -------------------------------------------------------------------------- */
/*                               Start Component                              */
/* -------------------------------------------------------------------------- */
export default function ImageEditorToolbox(props: Props): ReactNode {
  /* ------------------------- Non state data start ------------------------- */
  const { disabled = false } = props
  const {
    image,
    shapes,
    createRectangle,
    createEllipse,
    updateShape,
    shapeSelection,
    deleteShapes,
    undo,
    redo,
    previewSettings,
  } = useImageEditorContext()

  const log = new LogTool({ context: 'ImageEditorToolbox', logLevel: 'debug', enable: true })
  const { t } = useTranslation()

  // configuration variables
  const defaultShapeSize = 100 // use this as either width/height or radius
  const defaultShapePosition = { x: defaultShapeSize / 2, y: defaultShapeSize / 2 }
  /* -------------------------- Non state data end -------------------------- */

  /* --------------------------- Flag states start -------------------------- */

  /* ---------------------------- Flag states end --------------------------- */

  /* --------------------------- Data states start -------------------------- */
  const [selectedColor, setSelectedColor] = useState<string>('#ffffff')
  /* ---------------------------- Data states end --------------------------- */

  /* ----------------------------- Effects start ---------------------------- */

  /* ------------------------------ Effects end ----------------------------- */

  /* ------------------------ Utility functions start ----------------------- */
  /**
   * Given a background color hex value, returns the most appropriate foreground
   * color (black or white) for high contrast.
   * @param hexValue The color Hex value you plan on using as the background.
   */
  function getHighContrastColor(hexValue: string): 'black' | 'white' {
    const rgb = hexToRgba(hexValue)
    // Calculate YIQ brightness
    const yiq = (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
    // If brightness is greater than 128, use black; otherwise use white
    return yiq >= 128 ? 'black' : 'white'
  }
  /* ------------------------- Utility functions end ------------------------ */

  /* ----------------------- Callback functions start ----------------------- */
  const handleCreateRectangle = (): void => {
    const rectangleWidth = image.width ? image.width / 3 : defaultShapeSize
    const rectangleHeight = image.height ? image.height / 3 : defaultShapeSize
    createRectangle({
      x: image.width ? image.width / 2 - rectangleWidth / 2 : defaultShapePosition.x,
      y: image.height ? image.height / 2 - rectangleHeight / 2 : defaultShapePosition.y,
      width: rectangleWidth,
      height: rectangleHeight,
      fill: selectedColor,
      draggable: true,
    })
  }
  const handleCreateEllipse = (): void => {
    createEllipse({
      x: image.width ? image.width / 2 : defaultShapePosition.x,
      y: image.height ? image.height / 2 : defaultShapePosition.y,
      radiusX: image.width ? image.width / 4 : defaultShapeSize,
      radiusY: image.height ? image.height / 8 : defaultShapeSize / 2,
      fill: selectedColor,
      draggable: true,
    })
  }

  const handleColorChange = (color: string): void => {
    setSelectedColor(color)
    // update the color of selected shapes
    shapeSelection.ids.forEach(id => {
      // only update color if the color actually changed
      if (shapes[id].fill !== color) {
        updateShape(id, { fill: color })
      }
    })
  }

  const handlePreviewModeChange = (mode: 'solid' | 'xray' | 'outline'): void => {
    previewSettings.changeMode(mode)
  }

  /**
   * Deletes all selected shapes.
   */
  const handleDeleteSelectedShapes = (): void => {
    deleteShapes(shapeSelection.ids)
  }
  /* ------------------------ Callback functions end ------------------------ */

  /* ------------------------ Render constants start ------------------------ */
  const addPrimitiveShapeIcon = (borderRadius: string): ReactElement => (
    <Box
      width="20px"
      height="20px"
      border="1px solid"
      borderColor={getHighContrastColor(selectedColor)}
      borderRadius={borderRadius}
      boxShadow="0 0 4px rgba(0,0,0,0.1)"
      bgcolor={selectedColor}
      position="relative"
      sx={{
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '5px',
          bottom: '5px',
          left: 'calc(50% - 0.5px)',
          width: '0.5px',
          backgroundColor: getHighContrastColor(selectedColor),
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 'calc(50% - 0.5px)',
          height: '0.5px',
          left: '5px',
          right: '5px',
          backgroundColor: getHighContrastColor(selectedColor),
        },
      }}
    ></Box>
  )
  const createShapeTools: Tool[] = [
    {
      id: 'addRectangleTool',
      icon: addPrimitiveShapeIcon('0px'),
      onClick: handleCreateRectangle,
      tooltip: t('imageManipulation:interaction.tooltip.addRectangle'),
    },
    {
      id: 'addEllipseTool',
      icon: addPrimitiveShapeIcon('100%'),
      onClick: handleCreateEllipse,
      tooltip: t('imageManipulation:interaction.tooltip.addEllipse'),
    },
  ]
  const undoRedoTools: Tool[] = [
    {
      id: 'undoAction',
      icon: <UndoRounded />,
      onClick: undo,
      tooltip: t('common:interaction.tooltip.undo'),
    },
    {
      id: 'redoAction',
      icon: <RedoRounded />,
      onClick: redo,
      tooltip: t('common:interaction.tooltip.redo'),
    },
  ]
  /* ------------------------- Render constants end ------------------------- */

  /* ----------------------- Pre render actions start ----------------------- */

  /* ------------------------ Pre render actions end ------------------------ */

  /* ------------------------------------------------------------------------ */
  /*                             Render Component                             */
  /* ------------------------------------------------------------------------ */
  return (
    <Box
      display="flex"
      justifyContent="space-between"
      p="8px"
      sx={{
        border: '1px solid lightGray',
        borderRadius: '4px',
        backgroundColor: 'white',
        boxShadow: '1px 1px 15px -5px rgba(0,0,0,0.08)',
      }}
    >
      <Stack direction="row" spacing={1}>
        <ColorInput
          onChange={handleColorChange}
          onColorSquareClick={shapeSelection.ids && handleColorChange}
          disabled={disabled}
        />
        {createShapeTools.map(tool => (
          <Tooltip key={tool.id} title={tool.tooltip} arrow>
            <IconButton onClick={tool.onClick} disabled={disabled} sx={{ width: 40, height: 40 }}>
              {tool.icon}
            </IconButton>
          </Tooltip>
        ))}
        <Tooltip title={t('imageManipulation:interaction.tooltip.deleteSelectedShapes')} arrow>
          <IconButton
            onClick={handleDeleteSelectedShapes}
            disabled={disabled || !shapeSelection.ids}
          >
            <DeleteRounded />
          </IconButton>
        </Tooltip>
      </Stack>
      <Stack direction="row" spacing={1}>
        {undoRedoTools.map(tool => (
          <Tooltip key={tool.id} title={tool.tooltip} arrow>
            <IconButton onClick={tool.onClick} disabled={disabled}>
              {tool.icon}
            </IconButton>
          </Tooltip>
        ))}
      </Stack>
      <Stack direction="row" spacing={1}>
        <Box display="flex" alignItems="center">
          <Typography variant="body1">
            {t('imageManipulation:content.heading.previewMode')}:
          </Typography>
        </Box>
        <PreviewModeSelect onPreviewModeChange={handlePreviewModeChange} disabled={disabled} />
      </Stack>
    </Box>
  )
}
